


import { useState, useEffect } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import { baseUrl } from "../config/baseUrl"
import empty from "../assets/images/empty-box.png"
import Swal from "sweetalert2"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faQuestionCircle,
    faPlus,
    faCheck,
    faTimes,
    faSearch,
    faArrowLeft,
    faSave,
    faChevronLeft,
    faChevronRight,
    faEllipsisH,
} from "@fortawesome/free-solid-svg-icons"

export default function AssignQuestionList() {
    const { id } = useParams()
    const navigate = useNavigate()

    const [questions, setQuestions] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [statusFilter, setStatusFilter] = useState("active")
    const [language, setLanguage] = useState("english")
    const [testDetails, setTestDetails] = useState(null)
    const [loadingTest, setLoadingTest] = useState(true)
    const [selectedQuestions, setSelectedQuestions] = useState([])
    const [savingChanges, setSavingChanges] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)

    // filters
    const [subjectFilter, setSubjectFilter] = useState("")
    const [subjects, setSubjects] = useState([])
    const [topics, setTopics] = useState([])
    const [topicFilter, setTopicFilter] = useState("")

    // pagination states
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalQuestions, setTotalQuestions] = useState(0)

    useEffect(() => {
        fetchTestDetails()
    }, [id])

    const fetchTestDetails = async () => {
        try {
            setLoadingTest(true)
            const response = await axios.get(`${baseUrl}/test`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (response.data.status) {
                const testList = response.data.data || []
                const test = testList.find((t) => t._id === id)

                if (test) {
                    setTestDetails(test)
                    await fetchAssignedQuestions()
                    fetchAllData(1) // load first page questions
                } else {
                    Swal.fire({ title: "Error!", text: "Test not found", icon: "error" })
                    navigate("/test-series")
                }
            }
        } catch (error) {
            console.error("Error fetching test details:", error)
            Swal.fire({
                title: "Error!",
                text: "Failed to fetch test details",
                icon: "error",
            })
        } finally {
            setLoadingTest(false)
        }
    }

    const fetchAssignedQuestions = async () => {
        try {
            const response = await axios.post(
                `${baseUrl}/question_selected/test-questions`,
                { testId: id },
                {
                    headers: {
                        Authorization: localStorage.getItem("token"),
                        "Content-Type": "application/json",
                    },
                },
            )

            if (response.data.status) {
                const assignedQuestions = response.data.data || []
                const assignedQuestionIds = assignedQuestions.map((q) => q._id)
                setSelectedQuestions(assignedQuestionIds)
            }
        } catch (error) {
            console.error("Error fetching assigned questions:", error)
        }
    }
    useEffect(() => {
        fetchAllData(currentPage)
    }, [])
    const fetchAllData = async (page = 1) => {
        try {
            setLoading(true)

            const params = new URLSearchParams({
                page: page.toString(),
                keyword: searchTerm.trim(),
                status: statusFilter,
                subject: subjectFilter,
                topic: topicFilter,
                language,
            });


            const [questionsResponse, subjectsResponse, topicsResponse] = await Promise.all([
                fetch(`${baseUrl}/question/get?${params}`, {
                    headers: { Authorization: localStorage.getItem("token") || "" },
                }),
                fetch(`${baseUrl}/subject/list`, {
                    headers: { Authorization: localStorage.getItem("token") || "" },
                }),
                fetch(`${baseUrl}/topic/list`, {
                    headers: { Authorization: localStorage.getItem("token") || "" },
                }),
            ])

            const questionsData = await questionsResponse.json()
            const subjectsData = await subjectsResponse.json()
            const topicsData = await topicsResponse.json()

            if (questionsData.status && Array.isArray(questionsData.data)) {
                setQuestions(questionsData.data)
                setCurrentPage(questionsData.currentPage)
                setTotalPages(questionsData.totalPages)
                setTotalQuestions(questionsData.totalQuestions)
            } else {
                setQuestions([])
                console.error("Error Fetching Question List:", questionsData)
            }

            setSubjects(subjectsData?.data || [])
            setTopics(topicsData?.data || [])
        } catch (error) {
            console.error("Error fetching data:", error)
            console.error("Failed to fetch data")
            setQuestions([])
            setSubjects([])
            setTopics([])
        } finally {
            setLoading(false)
        }
    }

    // refetch whenever filter changes
    useEffect(() => {
        setCurrentPage(1)
        fetchAllData(1)
    }, [searchTerm, statusFilter, subjectFilter, topicFilter, language])

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber)
        fetchAllData(pageNumber)
    }

    const getFilteredTopics = () => {
        if (!subjectFilter) return topics;
        return topics.filter((topic) => {
            const topicSubjectId =
                typeof topic.subject === "object" ? topic.subject._id : topic.subject;
            return topicSubjectId === subjectFilter;
        });
    };
    const getQuestionText = (question, lang) => {
        if (lang === "english") return question?.question_english || question?.questions || ""
        if (lang === "hindi") return question?.question_hindi || question?.questions || ""
        if (lang === "bengali") return question?.question_bengali || question?.questions || ""
        return question?.question_english || question?.questions || ""
    }

    const isQuestionAssigned = (questionId) => selectedQuestions.includes(questionId)

    const handleToggleQuestion = (questionId) => {
        let updatedQuestions = [...selectedQuestions]
        if (updatedQuestions.includes(questionId)) {
            updatedQuestions = updatedQuestions.filter((id) => id !== questionId)
        } else {
            updatedQuestions.push(questionId)
        }
        setSelectedQuestions(updatedQuestions)
        setHasChanges(true)
    }

    const saveSelectedQuestions = async () => {
        try {
            setSavingChanges(true)
            const response = await axios.post(
                `${baseUrl}/question_selected`,
                { testID: id, questionsID: selectedQuestions },
                {
                    headers: {
                        Authorization: localStorage.getItem("token"),
                        "Content-Type": "application/json",
                    },
                },
            )

            if (response.data && response.data.status) {
                Swal.fire({
                    title: "Success!",
                    text: "Questions assigned to test successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false,
                })
                setHasChanges(false)
            } else {
                Swal.fire({
                    title: "Error!",
                    text: response.data?.message || "Failed to assign questions",
                    icon: "error",
                })
            }
        } catch (error) {
            console.error("Error saving selected questions:", error)
            Swal.fire({
                title: "Error!",
                text: "Failed to save question selections. Please try again.",
                icon: "error",
            })
        } finally {
            setSavingChanges(false)
        }
    }

    const truncateText = (text, maxLength = 50) => {
        if (!text) return ""
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
    }

    if (loadingTest) {
        return (
            <section className="main-sec">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading test details...</p>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    return (
        <section className="main-sec">
            <div className="container">
                {/* Header */}
                <div className="row">
                    <div className="col-lg-6">
                        <h4 className="dash-head">
                            <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                            Assign Questions to Test
                        </h4>
                    </div>
                    <div className="col-lg-6 d-flex justify-content-end align-items-start">
                        <Link to="/test-series" className="btn-add-question me-2">
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                            Back to Tests
                        </Link>
                        <button
                            className="btn-save-all"
                            onClick={saveSelectedQuestions}
                            disabled={!hasChanges || savingChanges}
                        >
                            {savingChanges ? (
                                <>
                                    <span className="spinner-border spinner-border-sm"></span>
                                    <span className="ms-2">Saving...</span>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} /> Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {testDetails && (
                    <div className="row mb-4">
                        <div className="col-lg-12">
                            <div className="test-info">
                                <h5>
                                    {testDetails.title}{" "}
                                    <span
                                        className={`badge ${testDetails.status === "active" ? "bg-success" : "bg-danger"
                                            }`}
                                    >
                                        {testDetails.status}
                                    </span>
                                </h5>
                                <p>
                                    <strong>Questions Assigned:</strong> {selectedQuestions.length}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="search-filter-container  mb-3">
                    <div className="search-container">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search questions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select value={statusFilter} className="filter-select" onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                    <select
                        className="filter-select"
                        value={subjectFilter}
                        onChange={(e) => {
                            setSubjectFilter(e.target.value);
                            setTopicFilter("");
                        }}
                    >
                        <option value="">All Subjects</option>
                        {subjects.map((subject) => (
                            <option key={subject._id} value={subject._id}>
                                {subject.name}
                            </option>
                        ))}
                    </select>

                    <select
                        className="filter-select"
                        value={topicFilter}
                        onChange={(e) => setTopicFilter(e.target.value)}
                    >
                        <option value="">All Topics</option>
                        {getFilteredTopics().map((topic) => (
                            <option key={topic._id} value={topic._id}>
                                {topic.name}
                            </option>
                        ))}
                    </select>
                    <select value={language} className="filter-select" onChange={(e) => setLanguage(e.target.value)}>
                        <option value="english">English</option>
                        <option value="hindi">Hindi</option>
                        <option value="bengali">Bengali</option>
                    </select>
                </div>

                {/* Questions Table */}
                <div className="table-responsive custom-table">
                    <table className="table table-borderless">
                        <thead>
                            <tr>
                                <th>SN</th>
                                <th>Question</th>
                                <th>Assigned</th>
                                <th>Subject</th>
                                <th>Topic</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="loading-spinner">
                                        <div className="spinner"></div>
                                    </td>
                                </tr>
                            ) : questions.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="empty-state">
                                        <img src={empty} alt="No questions" width="200px" />
                                        <h4>No Questions found!</h4>
                                    </td>
                                </tr>
                            ) : (
                                questions.map((q, i) => (
                                    <tr key={q._id} className={isQuestionAssigned(q._id) ? "question-row-assigned" : ""}>
                                        <td>{(currentPage - 1) * 50 + i + 1}</td>
                                        <td>
                                            <div className={`question-text ${isQuestionAssigned(q._id) ? "question-text-assigned" : ""}`}>
                                                {truncateText(getQuestionText(q, language), 60)}

                                            </div>

                                        </td>
                                        <td>

                                            {isQuestionAssigned(q._id) && (
                                                <span className="assigned-badge">
                                                    <FontAwesomeIcon icon={faCheck} /> Already Assigned
                                                </span>
                                            )}
                                        </td>
                                        <td>{q?.subject || "N/A"}</td>
                                        <td>{q?.topic || "N/A"}</td>
                                        <td>
                                            {q.status === "active" ? (
                                                <span className="status-badge status-active">
                                                    <FontAwesomeIcon icon={faCheck} /> Active
                                                </span>
                                            ) : (
                                                <span className="status-badge status-inactive">
                                                    <FontAwesomeIcon icon={faTimes} /> Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {isQuestionAssigned(q._id) ? (
                                                <button className="remove-btn" onClick={() => handleToggleQuestion(q._id)} disabled={savingChanges}>
                                                    <FontAwesomeIcon icon={faTimes} /> Remove
                                                </button>
                                            ) : (
                                                <button className="assign-btn" onClick={() => handleToggleQuestion(q._id)} disabled={savingChanges}>
                                                    <FontAwesomeIcon icon={faPlus} /> Assign
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination-container mt-3">
                        <nav aria-label="Questions pagination">
                            <div className="pagination-list">
                                <button
                                    className={`pagination-button ${currentPage === 1 ? "disabled" : ""}`}
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} className="me-1" /> Previous
                                </button>

                                {[...Array(totalPages)].map((_, index) => {
                                    const pageNumber = index + 1
                                    if (
                                        pageNumber === 1 ||
                                        pageNumber === totalPages ||
                                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={pageNumber}
                                                className={`pagination-button ${currentPage === pageNumber ? "active" : ""}`}
                                                onClick={() => paginate(pageNumber)}
                                            >
                                                {pageNumber}
                                            </button>
                                        )
                                    } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                                        return (
                                            <span key={pageNumber} className="pagination-button disabled">
                                                <FontAwesomeIcon icon={faEllipsisH} />
                                            </span>
                                        )
                                    }
                                    return null
                                })}

                                <button
                                    className={`pagination-button ${currentPage === totalPages ? "disabled" : ""}`}
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                                </button>
                            </div>
                        </nav>
                    </div>
                )}
            </div>

            <style >{`
        .main-sec {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
          padding: 2rem 0;
        }

        .dashboard-title {
          margin-bottom: 1.5rem;
        }

        .dash-head {
          color: #333;
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
        }

        .cards {
          background: white;
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .bus-filter {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #eee;
        }

        .card-title {
          color: #333;
          font-weight: 700;
          margin: 0;
        }

        .btn-add-test {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          color: white;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          margin-left: 0.5rem;
        }

        .btn-add-test:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
          color: white;
          text-decoration: none;
        }

        .search-filter-container {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
          align-items: center;
        }

        .search-container {
          position: relative;
        }

        .search-input {
          width: 100%;
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          padding-left: 2.5rem;
          font-size: 0.9rem;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }

        .filter-select {
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          width: 100%;
        }

        .custom-table {
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .table {
          margin: 0;
          width: 100%;
        }

        .table thead th {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-weight: 600;
          border: none;
          padding: 1rem 0.75rem;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table tbody td {
          padding: 0.75rem;
          vertical-align: middle;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.9rem;
        }

        .table tbody tr:hover {
          background-color: #f8f9ff;
        }

        .question-text {
          max-width: 200px;
          font-weight: 500;
          line-height: 1.4;
        }

        .subject-topic-cell {
          min-width: 120px;
        }

        .subject-name {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.75rem;
          background: #e3f2fd;
          color: #1976d2;
          border-radius: 15px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .topic-name {
          color: #666;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .correct-answer-cell {
          min-width: 100px;
        }

        .correct-answer-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.75rem;
          background: #e8f5e8;
          color: #2e7d32;
          border-radius: 15px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .correct-answer-text {
          color: #666;
          font-size: 0.75rem;
          line-height: 1.3;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .status-active {
          background: #d4edda;
          color: #155724;
        }

        .status-inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status-badge:hover {
          transform: scale(1.05);
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          font-size: 0.85rem;
        }

        .view-btn {
          background: #4facfe;
          color: white;
        }

        .edit-btn {
          background: #ffa726;
          color: white;
        }

        .delete-btn {
          background: #ef5350;
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        }

        .tooltip-text {
          position: absolute;
          bottom: -30px;
          left: 50%;
          transform: translateX(-50%);
          background: #333;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          white-space: nowrap;
          z-index: 10;
        }

        .action-btn:hover .tooltip-text {
          opacity: 1;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }

        .empty-state img {
          opacity: 0.7;
          margin-bottom: 1rem;
        }

        .empty-state h4 {
          color: #666;
          margin-bottom: 1rem;
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 3rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .pagination-container {
          margin-top: 2rem;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .pagination-list {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .pagination-button {
          padding: 0.5rem 1rem;
          border: 1px solid #ddd;
          background: white;
          color: #666;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .pagination-button:hover:not(:disabled) {
          background: #667eea;
          color: white;
          border-color: #667eea;
        }

        .pagination-button.active {
          background: #667eea;
          color: white;
          border-color: #667eea;
        }

        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
        }

        .modal-icon {
          width: 48px;
          height: 48px;
          background: #fff3cd;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          color: #856404;
          font-size: 1.2rem;
        }

        .modal-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: #333;
          margin: 0;
        }

        .modal-text {
          color: #666;
          margin-bottom: 2rem;
          line-height: 1.5;
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .modal-btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .modal-btn-cancel {
          background: #f8f9fa;
          color: #666;
        }

        .modal-btn-cancel:hover {
          background: #e9ecef;
        }

        .modal-btn-delete {
          background: #dc3545;
          color: white;
        }

        .modal-btn-delete:hover {
          background: #c82333;
        }

        @media (max-width: 1200px) {
          .search-filter-container {
            grid-template-columns: 1fr 1fr 1fr;
            gap: 0.75rem;
          }
        }

        @media (max-width: 768px) {
          .main-sec {
            padding: 1rem 0;
          }

          .cards {
            padding: 1rem;
          }

          .search-filter-container {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          .table-responsive {
            font-size: 0.8rem;
          }

          .question-text {
            max-width: 150px;
          }

          .action-buttons {
            flex-direction: column;
            gap: 0.25rem;
          }

          .action-btn {
            width: 28px;
            height: 28px;
            font-size: 0.75rem;
          }

          .dash-head {
            font-size: 1.4rem;
          }

          .btn-add-test {
            padding: 0.5rem 1rem;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 480px) {
          .table thead th,
          .table tbody td {
            padding: 0.5rem 0.25rem;
          }

          .question-text {
            max-width: 120px;
          }

          .subject-name,
          .correct-answer-badge {
            font-size: 0.7rem;
            padding: 0.2rem 0.5rem;
          }
        }
      `}</style>
            <style >{`
                .main-sec {
                    padding: 20px 0;
                }

                .dashboard-title {
                    margin-bottom: 15px;
                }

                .dash-head {
                    font-size: 24px;
                    color: #333;
                }

                .custom-bredcump {
                    margin-bottom: 20px;
                }

                .breadcrumb {
                    background-color: transparent;
                    padding: 0;
                }

                .breadcrumb-item a {
                    color: #007bff;
                    text-decoration: none;
                }

                .breadcrumb-item.active {
                    color: #6c757d;
                }

                .btn-add-question {
                    background-color: #28a745;
                    color: white;
                    padding: 10px 15px;
                    border: none;
                    border-radius: 5px;
                    cursor: pointer;
                    transition: background-color 0.3s;
                    text-decoration: none;
                    display: inline-flex;
                    align-items: center;
                }

                .btn-add-question:hover {
                    background-color: #218838;
                }

                .btn-save-all {
                    background-color: #007bff;
                    color: white;
                    padding: 10px 15px;
                    border: none;
                    border-radius: 5px;
                    cursor: pointer;
                    transition: background-color 0.3s;
                    display: inline-flex;
                    align-items: center;
                }

                .btn-save-all:disabled {
                    background-color: #ccc;
                    cursor: not-allowed;
                }

                .btn-save-all:hover:not(:disabled) {
                    background-color: #0056b3;
                }

                .test-info {
                    background-color: #f8f9fa;
                    padding: 15px;
                    border-radius: 5px;
                    margin-bottom: 20px;
                }

                .test-info h5 {
                    margin-bottom: 10px;
                    font-size: 20px;
                }

                .badge {
                    margin-left: 10px;
                    font-size: 14px;
                }

                .selection-summary {
                    background-color: #fff3cd;
                    padding: 15px;
                    border-radius: 5px;
                    margin-bottom: 20px;
                    color: #856404;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .selection-summary p {
                    margin-bottom: 0;
                }

                .cards {
                    border: none;
                    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
                    border-radius: 5px;
                }

                .bus-list {
                    padding: 20px;
                }

                .bus-filter {
                    margin-bottom: 20px;
                }

                .card-title {
                    font-size: 18px;
                    color: #333;
                }

                .search-filter-container {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 15px;
                }

                .search-container {
                    position: relative;
                    flex-grow: 1;
                }

                .search-icon {
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #aaa;
                }

                .search-input {
                    padding: 8px 10px 8px 30px;
                    border: 1px solid #ddd;
                    border-radius: 5px;
                    width: 100%;
                }

                .status-filter,
                .subject-filter {
                    padding: 8px 15px;
                    border: 1px solid #ddd;
                    border-radius: 5px;
                    width: auto;
                }

                .custom-table {
                    overflow-x: auto;
                }

                .table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .table thead th {
                    background-color: #f2f2f2;
                    padding: 12px;
                    text-align: left;
                    border-bottom: 2px solid #ddd;
                }

                .table tbody td {
                    padding: 12px;
                    border-bottom: 1px solid #eee;
                }

                .table tbody tr:hover {
                    background-color: #f9f9f9;
                }

                .status-badge {
                    padding: 5px 10px;
                    border-radius: 5px;
                    font-size: 14px;
                    display: inline-flex;
                    align-items: center;
                }

                .status-active {
                    background-color: #d4edda;
                    color: #155724;
                }

                .status-inactive {
                    background-color: #f8d7da;
                    color: #721c24;
                }

                .assign-btn,
                .remove-btn {
                    padding: 8px 12px;
                    border: none;
                    border-radius: 5px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    font-size: 14px;
                }

                .assign-btn {
                    background-color: #007bff;
                    color: white;
                }

                .assign-btn:hover {
                    background-color: #0056b3;
                }

                .remove-btn {
                    background-color: #dc3545;
                    color: white;
                }

                .remove-btn:hover {
                    background-color: #c82333;
                }

                .loading-spinner {
                    text-align: center;
                    padding: 20px;
                }

                .spinner {
                    border: 5px solid #f3f3f3;
                    border-top: 5px solid #3498db;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin 2s linear infinite;
                    margin: 0 auto;
                }

                .empty-state {
                    text-align: center;
                    padding: 20px;
                }

                .empty-state img {
                    margin-bottom: 15px;
                }

                .question-text {
                    max-width: 300px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .question-row-assigned {
                    background-color: #e8f5e9 !important;
                    border-left: 4px solid #4caf50;
                }

                .question-row-assigned:hover {
                    background-color: #c8e6c9 !important;
                }

                .assigned-badge {
                    background: #4caf50;
                    color: white;
                    padding: 0.2rem 0.5rem;
                    border-radius: 12px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    margin-left: 0.5rem;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .question-text-assigned {
                    font-weight: 600;
                    color: #2e7d32;
                }
                    .filter-select{
                    width: auto !important;
                    }
            `}</style>
        </section>
    )
}
